import { DestroyRef, inject, Injectable } from '@angular/core';

import { DBSchema, deleteDB, IDBPDatabase, openDB } from 'idb';
import { BehaviorSubject } from 'rxjs';

import { Guid } from '@models/base/helper-types';
import { IVzFilePreview, VzFile } from '@models/files/file';
import { VzTag } from '@models/tags/tag';
import { TaskTypeDef } from '@models/tasks/task-type-def';
import { Workflow } from '@models/workflow/workflow';
import { WorkflowBrief } from '@models/workflow/workflow-brief';
import { UrlPreview } from '@models/shared/url-preview';

const DEBUG = false;

const NAME = "vizorro_cache";
const VERSION = 7;

const LIMITS_CHECK_INTERVAL = 30 * 60;      // regular limits check interval, seconds
const REINIT_CHECK_INTERVAL = 250;
const REINIT_CHECK_INTERVAL_MAX = 30 * 1000;

interface VzDbSchema extends DBSchema {
    tags: {
        key: Guid,
        value: VzTag,
        indexes: {
            name: string,
            orgId: Guid,
            projectId: Guid,
            label: string,
            path: string,
        }
    },
    workflows: {
        key: Guid,
        value: WorkflowBrief | Workflow,
        indexes: {
            name: string,
            orgId: Guid,
            projectId: Guid,
        }
    },
    taskTypes: {
        key: Guid,
        value: TaskTypeDef,
        indexes: {
            name: string,
            orgId: Guid,
            projectId: Guid,
            workflowId: Guid,
        }
    },
    files: {
        key: Guid,
        value: VzFile,
        indexes: {
            name: string,
            orgId: Guid,
            size: number,
        }
    },
    files_la: {
        key: Guid,
        value: { id: Guid, dt: number },
        indexes: {
            lastAccess: number,
        }
    },
    filePreviews: {
        key: Guid,
        value: IVzFilePreview,
        indexes: {
            size: number,
            created: number,
        }
    },
    filePreviews_la: {
        key: Guid,
        value: { id: Guid, dt: number },
        indexes: {
            lastAccess: number,
        }
    },
    urlPreviews: {
        key: string,
        value: UrlPreview,
        indexes: {
            dt: number,
        }
    },
}

export enum DbCacheState {
    Unknown             = 0,
    Init                = 1,
    Upgrade             = 2,
    Blocked             = 3,
    Blocking            = 5,
    DelBlocked          = 6,
    Ready               = 10,
    Terminated          = 90,
    ServiceDestroing    = 91,
    Error               = 100,
}

const _w = DEBUG ? Function.prototype.bind.apply(console.warn, [console, '[DbCacheService]']) : () => {};
const _l = DEBUG ? Function.prototype.bind.apply(console.log, [console, '[DbCacheService]']) : () => {};

@Injectable({ providedIn: 'root' })
export class DbCacheService {

    private readonly _state = new BehaviorSubject(DbCacheState.Unknown);
    readonly state = this._state.asObservable();
    readonly getState = () => this._state.getValue();

    private _db?: IDBPDatabase<VzDbSchema>;
    private _error: any = undefined;
    private _clearSubscription?: ReturnType<typeof setTimeout>;
    private _reinitSubscription?: ReturnType<typeof setTimeout>;
    private _reinitInterval = REINIT_CHECK_INTERVAL;

    readonly getError = () => this._error;
    readonly isReady = () => !!this._db && this.getState() == DbCacheState.Ready;
    readonly getDb = () => this.isReady() ? this._db : undefined;

    private readonly _destroy = inject(DestroyRef);

    constructor() {
        _w('constructor');
        this.initDB();
        this._destroy.onDestroy(() => {
            this._state.next(DbCacheState.ServiceDestroing);
            if (this._clearSubscription) {
                clearInterval(this._clearSubscription);
                this._clearSubscription = undefined;
            }
        });
    }

    initDB(): void {
        _w('initDB');
        this._state.next(DbCacheState.Init);
        openDB<VzDbSchema>(NAME, VERSION, {
            upgrade: (db, oldVersion, newVersion, _tr, _event) => {
                this._state.next(DbCacheState.Upgrade);
                const oldVer = oldVersion ?? 0;
                _w('upgrade', `version(${oldVersion}): ${oldVer} -> ${newVersion}`);
                let error: any = undefined;

                // !=========== v5
                if (oldVersion < 5) {
                    try { db.deleteObjectStore('files'); } catch {}
                    try {
                        const st = db.createObjectStore('files', { keyPath: 'id' });
                        st.createIndex('name', 'name');
                        st.createIndex('orgId', 'orgId');
                        st.createIndex('size', 'size');
                    }
                    catch (err) {
                        _w('initDB, error while creating store "files":', err);
                        error = err;
                    }
                    try { db.deleteObjectStore('files_la'); } catch {}
                    try {
                        const st = db.createObjectStore('files_la', { keyPath: 'id' });
                        st.createIndex('lastAccess', 'dt');
                    }
                    catch (err) {
                        _w('initDB, error while creating store "files_la":', err);
                        error = err;
                    }

                    try { db.deleteObjectStore('filePreviews'); } catch {}
                    try {
                        const st = db.createObjectStore('filePreviews', { keyPath: 'id' });
                        st.createIndex('size', 'size');
                        st.createIndex('created', 'dt');
                    } catch (err) {
                        _w('initDB, error while creating store "filePreviews":', err);
                        error = err;
                    }
                    try { db.deleteObjectStore('filePreviews_la'); } catch {}
                    try {
                        const st = db.createObjectStore('filePreviews_la', { keyPath: 'id' });
                        st.createIndex('lastAccess', 'dt');
                    }
                    catch (err) {
                        _w('initDB, error while creating store "filePreviews":', err);
                        error = err;
                    }

                    try { db.deleteObjectStore('workflows'); } catch {}
                    try {
                        const st = db.createObjectStore('workflows', { keyPath: 'id' });
                        st.createIndex('name', 'name');
                        st.createIndex('orgId', 'orgId');
                        st.createIndex('projectId', 'projectId');
                    }
                    catch (err) {
                        _w('initDB, error while creating store "workflows":', err);
                        error = err;
                    }

                    try { db.deleteObjectStore('taskTypes'); } catch {}
                    try {
                        const st = db.createObjectStore('taskTypes', { keyPath: 'id' });
                        st.createIndex('name', 'name');
                        st.createIndex('orgId', 'orgId');
                        st.createIndex('projectId', 'projectId');
                        st.createIndex('workflowId', 'workflowId');
                    }
                    catch (err) {
                        _w('initDB, error while creating store "taskTypes":', err);
                        error = err;
                    }
                }

                // !=========== v6
                if (oldVersion < 6) {
                    try {
                        const st = db.createObjectStore('tags', { keyPath: 'id' });
                        st.createIndex('name', 'text');
                        st.createIndex('orgId', 'orgId');
                        st.createIndex('projectId', 'projectId');
                        st.createIndex('label', '_label');
                        st.createIndex('path', '_path');
                    }
                    catch (err) {
                        _w('initDB, error while creating store "tags":', err);
                        error = err;
                    }
                }

                // !=========== v7
                if (oldVersion < 7) {
                    try { db.deleteObjectStore('files'); } catch {}
                    try {
                        const st = db.createObjectStore('files', { keyPath: 'id' });
                        st.createIndex('name', 'name');
                        st.createIndex('orgId', 'orgId');
                        st.createIndex('size', 'size');
                    }
                    catch (err) {
                        _w('initDB, error while creating store "files":', err);
                        error = err;
                    }
                    try {
                        const st = db.createObjectStore('urlPreviews', { keyPath: 'id' });
                        st.createIndex('dt', 'dt');
                    }
                    catch (err) {
                        _w('initDB, error while creating store "urlPreviews":', err);
                        error = err;
                    }
                }

                // disable cache if schema upgrade failed
                if (error) {
                    this._error = error;
                    this._state.next(DbCacheState.Error);
                    _w('initDB, error while initializing, DbCache disabled. Error:', error);
                }
                else {
                    this._error = undefined;
                }
            },
            blocked: (currentVersion, blockedVersion, event) => {
                this._state.next(DbCacheState.Blocked);
                _w(`blocked, currentVersion: ${currentVersion}, blockedVersion: ${blockedVersion}, set state Blocked`, event);
            },
            blocking: (currentVersion, blockedVersion, event) => {
                this._state.next(DbCacheState.Blocking);
                _w(`blocking, currentVersion: ${currentVersion}, blockedVersion: ${blockedVersion}, closing DB...`, event);
                this._error = undefined;
                this._db?.close();
                this._setupReinitDB();
            },
            terminated: () => {
                this._state.next(DbCacheState.Terminated);
                _w('terminated');
                this._setupReinitDB();
            },
        }).then(db => {
            this._db = db;
            this._state.next(DbCacheState.Ready);
            _l('constructor, init DB complete:', db);
            if (this._reinitSubscription) {
                clearInterval(this._reinitSubscription);
                this._reinitSubscription = undefined;
            }
            this._checkLimits();
        }).catch(err => {
            this._db = undefined;
            this._error = err;
            this._state.next(DbCacheState.Error);
            _w('constructor, could not init DB:', err);
            if (this._reinitSubscription) {
                clearInterval(this._reinitSubscription);
                this._reinitSubscription = undefined;
            }
        });
    }

    private _setupReinitDB(first = false): void {
        if (this._reinitSubscription) {
            clearInterval(this._reinitSubscription);
            this._reinitSubscription = undefined;
        }
        if (first) {
            this._reinitInterval = REINIT_CHECK_INTERVAL;
        }
        else {
            this._reinitInterval *= 2;
            if (this._reinitInterval > REINIT_CHECK_INTERVAL_MAX) {
                this._reinitInterval = REINIT_CHECK_INTERVAL_MAX;
            }
        }
        _l(`_setupReinitDB, next check in: ${this._reinitInterval}ms:`, new Date(Date.now() + this._reinitInterval));
        this._reinitSubscription = setTimeout(() => this._tryReinitDB(), this._reinitInterval);
    }

    private _tryReinitDB(): void {
        if (this.getState() != DbCacheState.Ready) {
            this.initDB();
        }
    }

    async deleteDb() {
        this._state.next(DbCacheState.Terminated);
        await deleteDB(NAME, {
            blocked: (_currentVersion, _event) => {
                this._state.next(DbCacheState.DelBlocked);
                _w('logout, deleteDB blocked. There are other tabs using DB, skip deletion.');
            }
        });
        this._db?.close();
        this._db = undefined;
    }


    // !############ VzFile
    async getFile(id: Guid): Promise<VzFile | undefined> {
        const db = this.getDb();
        if (db) {
            let item: VzFile | undefined;
            try {
                item = await db.get('files', id);
                if (item) {
                    this._updateLA('files_la', [id]);
                }
            } catch {}
            return Promise.resolve(item);
        }
        return Promise.resolve(undefined);
    }

    async getFiles(ids: Guid[]): Promise<VzFile[] | undefined> {
        const db = this.getDb();
        // _l('getFiles', ids, 'db:', db);
        if (db) {
            const upIds: Guid[] = [];
            try {
                const items: VzFile[] = [];
                const tx = db.transaction('files');
                const store = tx.store;
                for (const id of ids) {
                    // _l('getFiles', 'getting:', id);
                    const item = await store.get(id);
                    // _l('getFiles', '\t\t got');
                    if (item) {
                        items.push(item);
                        upIds.push(id);
                    }
                }
                await tx.done;
                this._updateLA('files_la', ids);
                // _l('getFiles', 'return items:', items);
                return Promise.resolve(items);
            } catch {}
        }
        return Promise.resolve(undefined);
    }

    async putFiles(items: VzFile[]): Promise<void> {
        const db = this.getDb();
        if (db) {
            const tx = db.transaction(['files', 'files_la'], 'readwrite');
            await Promise.all([
                ...items.map(x => tx.objectStore('files').put(x)),
                ...items.map(x => tx.objectStore('files_la').put({ id: x.id!, dt: Date.now() })),
                tx.done
            ]);
        }
        return Promise.resolve();
    }

    async removeFiles(ids: Guid[]): Promise<void> {
        const db = this.getDb();
        if (db) {
            const tx = db.transaction(['files', 'files_la'], 'readwrite');
            await Promise.all([
                ...ids.map(id => tx.objectStore('files').delete(id)),
                ...ids.map(id => tx.objectStore('files_la').delete(id)),
                tx.done
            ]);
        }
        return Promise.resolve();
    }

    async clearFiles(untrusted?: boolean): Promise<void> {
        const db = this.getDb();
        if (db && !untrusted) {
            try {
                await db.clear('files');
                await db.clear('files_la');
            } catch {}
        }
    }

    async _updateLA(sn: 'files_la' | 'filePreviews_la', ids: Guid[]): Promise<void> {
        if (ids.length){
            const db = this.getDb();
            // _l('_updateLA', ids, 'db:', db);
            if (db) {
                try {
                    const tx = db.transaction(sn, 'readwrite');
                    const store = tx.store;
                    const dt = Date.now();
                    for (const id of ids) {
                        // _l('_updateLA', 'update item la:', id);
                        await store.put( { id, dt });
                    }
                    await tx.done
                } catch {}
            }
        }
    }


    // !############ IVzFilePreview ({ blob })
    async getFilePreview(id: Guid): Promise<IVzFilePreview | undefined> {
        const db = this.getDb();
        if (db) {
            let item: IVzFilePreview | undefined;
            try {
                item = await db.get('filePreviews', id);
                if (item) {
                    this._updateLA('filePreviews_la', [id]);
                }
            } catch {}
            return Promise.resolve(item);
        }
        return Promise.resolve(undefined);
    }

    async getFilePreviews(ids: Guid[]): Promise<IVzFilePreview[] | undefined> {
        const db = this.getDb();
        // _l('getFilePreviews', ids, 'db:', db);
        if (db) {
            const upIds: Guid[] = [];
            try {
                const items: IVzFilePreview[] = [];
                const tx = db.transaction('filePreviews');
                const store = tx.store;
                for (const id of ids) {
                    // _l('getFilePreviews', 'getting:', id);
                    const item = await store.get(id);
                    // _l('getFilePreviews', '\t\t got');
                    if (item) {
                        items.push(item);
                        upIds.push(id);
                    }
                }
                await tx.done;
                this._updateLA('filePreviews_la', upIds);
                // _l('getFiles', 'return items:', items);
                return Promise.resolve(items);
            } catch {}
        }
        return Promise.resolve(undefined);
    }

    async putFilePreviews(items: IVzFilePreview[]): Promise<void> {
        const db = this.getDb();
        if (db) {
            const tx = db.transaction(['filePreviews', 'filePreviews_la'], 'readwrite');
            await Promise.all([
                ...items.map(x => tx.objectStore('filePreviews').put({ ...x, size: x.size ?? (x.blob?.size || 0), dt: x.dt ?? Date.now() })),
                ...items.map(x => tx.objectStore('filePreviews_la').put({ id: x.id!, dt: Date.now() })),
                tx.done
            ]);
        }
        return Promise.resolve();
    }

    async removeFilePreviews(ids: Guid[]): Promise<void> {
        const db = this.getDb();
        if (db) {
            const tx = db.transaction(['filePreviews', 'filePreviews_la'], 'readwrite');
            await Promise.all([
                ...ids.map(id => tx.objectStore('filePreviews').delete(id)),
                ...ids.map(id => tx.objectStore('filePreviews_la').delete(id)),
                tx.done
            ]);
        }
        return Promise.resolve();
    }

    async clearFilePreviews(untrusted?: boolean): Promise<void> {
        const db = this.getDb();
        if (db && !untrusted) {
            try {
                await db.clear('filePreviews');
                await db.clear('filePreviews_la');
            } catch {}
        }
    }



    // !############ TaskTypeDef
    async getTaskType(id: Guid): Promise<TaskTypeDef | undefined> {
        const db = this.getDb();
        if (db) {
            let item: TaskTypeDef | undefined;
            try {
                item = await db.get('taskTypes', id);
            } catch {}
            return Promise.resolve(item);
        }
        return Promise.resolve(undefined);
    }

    async getTaskTypes(ids: Guid[]): Promise<TaskTypeDef[] | undefined> {
        const db = this.getDb();
        if (db) {
            const items: TaskTypeDef[] = [];
            for (const id of ids) {
                try {
                    const item = await db.get('taskTypes', id);
                    if (item) {
                        items.push(item);
                    }
                } catch {}
            }
            return Promise.resolve(items);
        }
        return Promise.resolve(undefined);
    }

    async getTaskTypes4project(pid: Guid): Promise<TaskTypeDef[] | undefined> {
        const db = this.getDb();
        if (db) {
            const items: TaskTypeDef[] = [];
            try {
                const index = db.transaction('taskTypes').store.index('projectId');
                for await (const cursor of index.iterate(pid)) {
                    const item = cursor.value;
                    if (item) {
                        items.push(item);
                    }
                }
            } catch {}
            return Promise.resolve(items);
        }
        return Promise.resolve(undefined);
    }

    async putTaskTypes(items: TaskTypeDef[]): Promise<void> {
        const db = this.getDb();
        if (db) {
            const tx = db.transaction('taskTypes', 'readwrite');
            await Promise.all([
                ...items.map(x => tx.store.put(x)),
                tx.done
            ]);
        }
        return Promise.resolve();
    }

    async removeTaskTypes(ids: Guid[]): Promise<void> {
        const db = this.getDb();
        if (db) {
            const tx = db.transaction('taskTypes', 'readwrite');
            await Promise.all([
                ...ids.map(id => tx.store.delete(id)),
                tx.done
            ]);
        }
        return Promise.resolve();
    }

    async clearTaskTypes(_untrusted?: boolean): Promise<void> {
        const db = this.getDb();
        if (db) {
            try {
                await db.clear('taskTypes');
            } catch {}
        }
    }



    // !############ Workflow / WorkflowBrief
    async getWorkflow(id: Guid): Promise<Workflow | WorkflowBrief | undefined> {
        const db = this.getDb();
        if (db) {
            let item: Workflow | WorkflowBrief | undefined;
            try {
                item = await db.get('workflows', id);
            } catch {}
            return Promise.resolve(item);
        }
        return Promise.resolve(undefined);
    }

    async getWorkflows(ids: Guid[]): Promise<(Workflow | WorkflowBrief)[] | undefined> {
        const db = this.getDb();
        if (db) {
            const items: (Workflow | WorkflowBrief)[] = [];
            for (const id of ids) {
                try {
                    const item = await db.get('workflows', id);
                    if (item) {
                        items.push(item);
                    }
                } catch {}
            }
            return Promise.resolve(items);
        }
        return Promise.resolve(undefined);
    }

    async getWorkflows4project(pid: Guid): Promise<(Workflow | WorkflowBrief)[] | undefined> {
        const db = this.getDb();
        if (db) {
            const items: (Workflow | WorkflowBrief)[] = [];
            try {
                const index = db.transaction('workflows').store.index('projectId');
                for await (const cursor of index.iterate(pid)) {
                    const item = cursor.value;
                    if (item) {
                        items.push(item);
                    }
                }
            } catch {}
            return Promise.resolve(items);
        }
        return Promise.resolve(undefined);
    }

    async putWorkflows(items: (Workflow | WorkflowBrief)[]): Promise<void> {
        const db = this.getDb();
        if (db) {
            const tx = db.transaction('workflows', 'readwrite');
            await Promise.all([
                ...items.map(x => tx.store.put(x)),
                tx.done
            ]);
        }
        return Promise.resolve();
    }

    async removeWorkflows(ids: Guid[]): Promise<void> {
        const db = this.getDb();
        if (db) {
            const tx = db.transaction('workflows', 'readwrite');
            await Promise.all([
                ...ids.map(id => tx.store.delete(id)),
                tx.done
            ]);
        }
        return Promise.resolve();
    }

    async clearWorkflows(_untrusted?: boolean): Promise<void> {
        const db = this.getDb();
        if (db) {
            try {
                await db.clear('workflows');
            } catch {}
        }
    }


    // !############ VzTag
    async getTag(id: Guid): Promise<VzTag | undefined> {
        const db = this.getDb();
        if (db) {
            let item: VzTag | undefined;
            try {
                item = await db.get('tags', id);
            } catch {}
            return Promise.resolve(item);
        }
        return Promise.resolve(undefined);
    }

    async getTags(ids: Guid[]): Promise<VzTag[] | undefined> {
        const db = this.getDb();
        if (db) {
            const items: VzTag[] = [];
            for (const id of ids) {
                try {
                    const item = await db.get('tags', id);
                    if (item) {
                        items.push(item);
                    }
                } catch {}
            }
            return Promise.resolve(items);
        }
        return Promise.resolve(undefined);
    }

    async getTags4project(pid: Guid): Promise<VzTag[] | undefined> {
        const db = this.getDb();
        if (db) {
            const items: VzTag[] = [];
            try {
                const index = db.transaction('tags').store.index('projectId');
                for await (const cursor of index.iterate(pid)) {
                    const item = cursor.value;
                    if (item) {
                        items.push(item);
                    }
                }
            } catch {}
            return Promise.resolve(items);
        }
        return Promise.resolve(undefined);
    }

    async putTags(items: VzTag[]): Promise<void> {
        const db = this.getDb();
        if (db) {
            const tx = db.transaction('tags', 'readwrite');
            await Promise.all([
                ...items.map(x => tx.store.put(x)),
                tx.done
            ]);
        }
        return Promise.resolve();
    }

    async removeTags(ids: Guid[]): Promise<void> {
        const db = this.getDb();
        if (db) {
            const tx = db.transaction('tags', 'readwrite');
            await Promise.all([
                ...ids.map(id => tx.store.delete(id)),
                tx.done
            ]);
        }
        return Promise.resolve();
    }

    async clearTags(_untrusted?: boolean): Promise<void> {
        const db = this.getDb();
        if (db) {
            try {
                await db.clear('tags');
            } catch {}
        }
    }


    // !############ UrlPreview
    async getUrlPreview(id: Guid): Promise<UrlPreview | undefined> {
        const db = this.getDb();
        if (db) {
            let item: UrlPreview | undefined;
            try {
                item = await db.get('urlPreviews', id);
            } catch {}
            return Promise.resolve(item);
        }
        return Promise.resolve(undefined);
    }

    async getUrlPreviews(ids: Guid[]): Promise<UrlPreview[] | undefined> {
        const db = this.getDb();
        if (db) {
            const items: UrlPreview[] = [];
            for (const id of ids) {
                try {
                    const item = await db.get('urlPreviews', id);
                    if (item) {
                        items.push(item);
                    }
                } catch {}
            }
            return Promise.resolve(items);
        }
        return Promise.resolve(undefined);
    }

    async putUrlPreviews(items: UrlPreview[]): Promise<void> {
        const db = this.getDb();
        if (db) {
            const tx = db.transaction('urlPreviews', 'readwrite');
            await Promise.all([
                ...items.map(x => tx.store.put(x)),
                tx.done
            ]);
        }
        return Promise.resolve();
    }

    async removeUrlPreviews(ids: Guid[]): Promise<void> {
        const db = this.getDb();
        if (db) {
            const tx = db.transaction('urlPreviews', 'readwrite');
            await Promise.all([
                ...ids.map(id => tx.store.delete(id)),
                tx.done
            ]);
        }
        return Promise.resolve();
    }

    async clearUrlPreviews(_untrusted?: boolean): Promise<void> {
        const db = this.getDb();
        if (db) {
            try {
                await db.clear('urlPreviews');
            } catch {}
        }
    }



    // !########## internal

    private async _checkLimits() {
        // _w('_checkLimits');
        try {
            const est = await navigator.storage.estimate();
            // _l('_checkLimits, storage limits  estimates:', est);
            if (est?.usage && est.quota) {
                const free = Math.round(100 - (est.usage / est.quota) * 100);
                const db = this.getDb();
                // _l('_checkLimits, free percents:', free, 'db:', db);
                if (db) {
                    { // filePreviews
                        const LIMIT = 3 * 24 * 60 * 60;
                        const LIMIT_HARD = 1 * 24 * 60 * 60;
                        const HARD_LIMIT_PERCENT = 15;

                        const clearDt = Date.now() - (free < HARD_LIMIT_PERCENT ? LIMIT_HARD : LIMIT) * 1000;
                        // _l(`_checkLimits, <filePreviews> LIMIT/LIMIT_HARD: ${LIMIT}/${LIMIT_HARD}, clearDt:`, clearDt, `(${new Date(clearDt)})`);
                        const ids1 = (await db.getAllKeysFromIndex('filePreviews_la', 'lastAccess', IDBKeyRange.upperBound(clearDt)));
                        const ids2 = (await db.getAllKeysFromIndex('filePreviews', 'size', IDBKeyRange.upperBound(1)));
                        const ids = Object.keys([...ids1, ...ids2].reduce((obj, id) => ({ ...obj, [id]: 1 }), {}));
                        // _l('_checkLimits, <filePreviews> ids to delete:', ids);
                        if (ids.length) {
                            {
                                const tx = await db.transaction('filePreviews_la', 'readwrite');
                                await Promise.all(ids.map(id => tx.store.delete(id)))
                                await tx.done;
                            }
                            {
                                const tx = await db.transaction('filePreviews', 'readwrite');
                                await Promise.all(ids.map(id => tx.store.delete(id)))
                                await tx.done;
                            }
                        }
                    }
                    { // files
                        const LIMIT = 14 * 24 * 60 * 60;
                        const LIMIT_HARD = 1 * 24 * 60 * 60;
                        const HARD_LIMIT_PERCENT = 15;

                        const clearDt = Date.now() - (free < HARD_LIMIT_PERCENT ? LIMIT_HARD : LIMIT) * 1000;
                        // _l(`_checkLimits, <files> LIMIT/LIMIT_HARD: ${LIMIT}/${LIMIT_HARD}, clearDt:`, clearDt, `(${new Date(clearDt)})`);
                        const ids = (await db.getAllKeysFromIndex('files_la', 'lastAccess', IDBKeyRange.upperBound(clearDt)));
                        // _l('_checkLimits, <files> ids to delete:', ids);
                        if (ids.length) {
                            {
                                const tx = await db.transaction('files_la', 'readwrite');
                                await Promise.all(ids.map(id => tx.store.delete(id)))
                                await tx.done;
                            }
                            {
                                const tx = await db.transaction('files', 'readwrite');
                                await Promise.all(ids.map(id => tx.store.delete(id)))
                                await tx.done;
                            }
                        }
                    }
                    { // urlPreviews
                        const LIMIT = 60 * 60;
                        const LIMIT_HARD = 30 * 60;
                        const HARD_LIMIT_PERCENT = 15;

                        const clearDt = Date.now() - (free < HARD_LIMIT_PERCENT ? LIMIT_HARD : LIMIT) * 1000;
                        _l(`_checkLimits, <urlPreviews> LIMIT/LIMIT_HARD: ${LIMIT}/${LIMIT_HARD}, clearDt:`, clearDt, `(${new Date(clearDt)})`);
                        const ids = (await db.getAllKeysFromIndex('urlPreviews', 'dt', IDBKeyRange.upperBound(clearDt)));
                        _l('_checkLimits, <urlPreviews> ids to delete:', ids);
                        if (ids.length) {
                            {
                                const tx = await db.transaction('urlPreviews', 'readwrite');
                                await Promise.all(ids.map(id => tx.store.delete(id)))
                                await tx.done;
                            }
                        }
                    }
                }
            }
            this._setupLimitsCheck();
        }
        catch (err) {
            _w('_checkLimits, error:', err);
            this._setupLimitsCheck();
        }
    }

    private _setupLimitsCheck(): void {
        if (this._clearSubscription) {
            clearInterval(this._clearSubscription);
            this._clearSubscription = undefined;
        }
        _l(`_setupLimitsCheck, next check in: ${LIMITS_CHECK_INTERVAL} seconds:`, new Date(Date.now() + LIMITS_CHECK_INTERVAL * 1000));
        this._clearSubscription = setTimeout(() => this._checkLimits(), LIMITS_CHECK_INTERVAL * 1000);
    }

}
